import React from 'react'
import { Alert, Button, Collapse, type CollapseProps, Popconfirm, Typography } from 'antd'
import { createSelector } from '@reduxjs/toolkit'
import { useTranslation } from 'react-i18next'

import { CustomModal } from '../../common/CustomModal'
import { useAppDispatch, useAppSelector } from '../../../core/hooks'
import { CardinalApiState, clearParentRecoveryKey } from '../../../core/services/auth.api'
import { CopyJSONButton } from '../../common/CopyJSONButton'
import './index.css'

const reduxSelector = createSelector(
  (state: { cardinalApi: CardinalApiState }) => state.cardinalApi,
  (cardinalApi: CardinalApiState) => ({
    parentRecoveryKey: cardinalApi.parentRecoveryKey,
    parentHcpName: cardinalApi.parentHcpName,
  }),
)

export const ModalParentRecoveryKey = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { parentRecoveryKey, parentHcpName } = useAppSelector(reduxSelector)

  const jsonTitle = `RetinoBridge_ParentRecoveryKey_${(parentHcpName ?? 'Parent').replace(/\s+/g, '_')}`
  const jsonData = {
    RetinoBridge: {
      accountType: 'parent',
      accountName: parentHcpName ?? 'Parent',
      recoveryKey: parentRecoveryKey,
    },
  }

  const handleClose = () => dispatch(clearParentRecoveryKey())

  const getCustomFooter = () => [
    <Popconfirm
      key="confirmClose"
      title={t('auth.parentKeyInit.recoveryKey.popconfirm.title')}
      description={
        <div>
          <p>{t('auth.parentKeyInit.recoveryKey.popconfirm.description1')}</p>
          <p>{t('auth.parentKeyInit.recoveryKey.popconfirm.description2')}</p>
        </div>
      }
      onConfirm={handleClose}
      okText={t('auth.parentKeyInit.recoveryKey.popconfirm.okText')}
      okButtonProps={{ danger: true }}
      cancelText={t('auth.parentKeyInit.recoveryKey.popconfirm.cancelText')}
      placement="topRight"
    >
      <Button key="back">{t('auth.parentKeyInit.recoveryKey.buttons.close')}</Button>
    </Popconfirm>,
    <Button key="submit" type="primary">
      <CopyJSONButton jsonData={jsonData} jsonName={jsonTitle} buttonTitle={t('auth.parentKeyInit.recoveryKey.buttons.downloadKeyFile')} />
    </Button>,
  ]

  const items: CollapseProps['items'] = [
    {
      key: '1',
      label: t('user.modalRecoveryKey.collapse.item1.label'),
      children: <p>{t('user.modalRecoveryKey.collapse.item1.text')}</p>,
    },
    {
      key: '2',
      label: t('user.modalRecoveryKey.collapse.item2.label'),
      children: (
        <p>
          {t('user.modalRecoveryKey.collapse.item2.text_part1')}
          <strong>{t('user.modalRecoveryKey.collapse.item2.text_strong')}</strong>
          {t('user.modalRecoveryKey.collapse.item2.text_part2')}
        </p>
      ),
    },
    {
      key: '3',
      label: t('user.modalRecoveryKey.collapse.item4.label'),
      children: <p>{t('user.modalRecoveryKey.collapse.item4.text')}</p>,
    },
  ]

  return (
    <CustomModal closable={false} isVisible={!!parentRecoveryKey} title={t('auth.parentKeyInit.recoveryKey.title')} customFooter={getCustomFooter()}>
      <div className="modalParentRecoveryKey">
        <div className="modalParentRecoveryKey__content">
          <div>
            <p>{t('auth.parentKeyInit.recoveryKey.intro_line1', { parentName: parentHcpName ?? '' })}</p>
            <p>{t('auth.parentKeyInit.recoveryKey.intro_line2')}</p>
          </div>
        </div>

        <Alert
          type="info"
          message={t('auth.parentKeyInit.recoveryKey.alert_message')}
          description={
            <Typography.Paragraph style={{ margin: 0 }} className="values-token" copyable={{ text: parentRecoveryKey }}>
              <strong>{parentRecoveryKey?.toUpperCase()}</strong>
            </Typography.Paragraph>
          }
        />
        <Collapse size="small" items={items} />
      </div>
    </CustomModal>
  )
}
